import React, {
    useState,
    useEffect,
} from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import Select, { SingleValue } from 'react-select'
import {
    createUser,
    readCompanies,
    updateUser,
    User,
    Company,
} from '../helpers'
import { withConnectWrapper } from '../../../../validator'

type AnyProps = Record<string, unknown>

interface SelectOption {
    value: string
    label: string
}

interface AlarmSettings {
    activation: boolean
    deactivation: boolean
    oilLevel: boolean
    pressure: boolean
    sensorDirty: boolean
    sensorMode: boolean
    service: boolean
    timeMode: boolean
    voltage: boolean
    deviceProblemReporting: boolean
}

interface UserFormData {
    name: string
    surname: string
    company: string
    email: string
    phone: string
    superAdmin: boolean
    alarmsNotifiable: boolean
    alarms: AlarmSettings
}

const initAlarms: AlarmSettings = {
    activation: true,
    deactivation: true,
    oilLevel: true,
    pressure: true,
    sensorDirty: true,
    sensorMode: true,
    service: true,
    timeMode: true,
    voltage: true,
    deviceProblemReporting: true,
}

const initUserForm: UserFormData = {
    name: '',
    surname: '',
    company: '',
    email: '',
    phone: '',
    superAdmin: false,
    alarmsNotifiable: true,
    alarms: {
        ...initAlarms,
    },
}

interface UserFormProps {
    companies: Company[]
    updatedUser?: User | null
    successAction?: () => void
}

/**
 * Form component for creating and updating users.
 *
 * @param {UserFormProps} props
 * @return {JSX.Element}
 */
function UserForm(props: UserFormProps): JSX.Element {
    const { companies, updatedUser, successAction } = props
    const { t } = useTranslation()

    const [companiesToSelect, setCompaniesToSelect] = useState<SelectOption[]>([])
    const [userForm, setUserForm] = useState<UserFormData>({ ...initUserForm })

    useEffect(() => {
        readCompanies()
            .then(() => {
                setCompaniesToSelect(
                    companies.map(company => ({
                        value: company.uid,
                        label: company.name,
                    })),
                )
            })

        if (updatedUser) {
            setUserForm(updatedUser as unknown as UserFormData)
        }
    }, [companies, updatedUser])

    /**
     * Set user form data
     *
     * @param {keyof UserFormData} key
     * @param {string | boolean} value
     */
    const setUserFormData = (key: keyof UserFormData, value: string | boolean): void => {
        setUserForm(prev => ({
            ...prev,
            [key]: value,
        }))
    }

    /**
     * Set user form alarms
     *
     * @param {keyof AlarmSettings} key
     * @param {boolean} value
     */
    const setUserFormAlarms = (key: keyof AlarmSettings, value: boolean): void => {
        setUserForm(prev => ({
            ...prev,
            alarms: {
                ...prev.alarms,
                [key]: value,
            },
        }))
    }

    return (
        <div className="d-flex flex-column p-3">
            <div className="form-group">
                <label htmlFor="user-form-email">
                    {t('users.form.email')} *
                </label>
                <input
                    type="email"
                    className="form-control"
                    id="user-form-email"
                    placeholder={t('users.form.email')}
                    value={userForm.email || ''}
                    disabled={!!updatedUser}
                    onChange={event => setUserFormData('email', event.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="user-form-name">
                    {t('users.form.name')}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="user-form-surname"
                    placeholder={t('users.form.name')}
                    value={userForm.name || ''}
                    onChange={event => setUserFormData('name', event.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="user-form-surname">
                    {t('users.form.surname')}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="user-form-surname"
                    placeholder={t('users.form.surname')}
                    value={userForm.surname || ''}
                    onChange={event => setUserFormData('surname', event.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="user-form-phone">
                    {t('users.form.phone')}
                </label>
                <input
                    type="phone"
                    className="form-control"
                    id="user-form-phone"
                    placeholder={t('users.form.phone')}
                    value={userForm.phone || ''}
                    onChange={event => setUserFormData('phone', event.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="user-form-name">
                    {t('users.form.company')}
                </label>
                <Select
                    value={companiesToSelect.find(company => company.value === userForm.company) || null}
                    onChange={(selectedOption: SingleValue<SelectOption>) => {
                        if (selectedOption) {
                            setUserFormData('company', selectedOption.value)
                        }
                    }}
                    options={companiesToSelect}
                    placeholder={t('users.form.company')}
                />
            </div>
            <div className="form-group">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="user-form-super-admin"
                        onChange={event => setUserFormData('superAdmin', event.target.checked)}
                        checked={userForm.superAdmin || false}
                    />
                    <label className="form-check-label" htmlFor="user-form-super-admin">
                        {t('users.form.superAdmin')}
                    </label>
                </div>
            </div>
            <div className="form-group">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="user-form-alarms-notifiable"
                        onChange={event => setUserFormData('alarmsNotifiable', event.target.checked)}
                        checked={userForm.alarmsNotifiable || false}
                    />
                    <label className="form-check-label" htmlFor="user-form-alarms-notifiable">
                        {t('users.form.alarmsNotifiable')}
                    </label>
                </div>
            </div>
            {userForm.alarmsNotifiable && (
                <div className="card">
                    <div className="card-body">
                        <h6 className="card-subtitle mb-2 text-muted">{t('users.form.alarmsSettings')}</h6>
                        {Object.entries(userForm.alarms || initAlarms).map(([alarmName, alarmValue]) => (
                            <div className="form-group" key={alarmName}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`form-check-${alarmName}`}
                                        onChange={event => setUserFormAlarms(alarmName as keyof AlarmSettings, event.target.checked)}
                                        checked={alarmValue || false}
                                    />
                                    <label className="form-check-label" htmlFor={`form-check-${alarmName}`}>
                                        {t(`users.form.alarm.${alarmName}`)}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="form-group row">
                <div className="col-sm-10">
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => {
                            (updatedUser ? updateUser(updatedUser as User, userForm as unknown as Partial<User>) : createUser(userForm as unknown as User))
                                .then(() => {
                                    if (typeof successAction === 'function') {
                                        successAction()
                                    }
                                    setUserForm({ ...initUserForm })
                                    window.swalert(t('users.form.saveSuccess'), 'success')
                                })
                                .catch((error: unknown) => window.swalert(String(error), 'danger'))
                        }}
                    >
                        {t('users.form.save')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default withConnectWrapper((state: { superAdmin: { companies: Company[] } }) => ({
    companies: state.superAdmin.companies,
}))(UserForm as unknown as ComponentType<AnyProps>)
